import {
    HStack,
    VStack,
    Text,
    Tooltip,
    Tag,
    Spinner,
    ModalOverlay,
    ModalHeader,
    ModalContent,
    Modal,
    ModalBody,
    ModalCloseButton,
    PopoverTrigger,
    Popover,
    PopoverBody,
    PopoverContent,
    Box,
    StackDivider,
    Grid,
    GridItem,
    Tabs,
    Tab,
    TabList,
    TabPanels,
    TabPanel,
    Icon,
    FormControl,
    FormLabel,
    Input,
    InputGroup,
    InputProps,
    IconButton,
    Button,
    PopoverCloseButton,
    PopoverHeader,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    AvatarGroup,
    Avatar,
    ButtonGroup,
    Heading,
    BoxProps,
    StackProps,
    Switch,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    Radio,
    useDisclosure,
    Checkbox,
    chakra,
    Badge,
} from '@chakra-ui/react';
import {
    HiDotsHorizontal,
    HiOutlineDocumentText,
    HiOutlineDotsHorizontal,
    HiOutlineTrash,
    HiPlus,
    HiOutlineCheckCircle,
} from 'react-icons/hi';
import { AiOutlineTable } from 'react-icons/ai';
import { BsThreeDots, BsThreeDotsVertical } from 'react-icons/bs';
import React from 'react';
import { HiOutlineSlash } from 'react-icons/hi2';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { ReportingViewBaseConfig } from '../../../base';
import { DocumentFormViewProps } from './documentFormProps';
import TextareaAutosize from 'react-textarea-autosize';
import { FormBuilderContainerProps } from './builder';
import { FormCollectionContainerProps } from './collection';

const ChakraTextareaAutosize = chakra(TextareaAutosize, {
    baseStyle: {
        // any default Chakra styles you want here
        // e.g. fontSize: "md", etc.
    },
});

export function createDocumentFormView(
    config: ReportingViewBaseConfig & {
        Container: {
            Builder: React.FC<FormBuilderContainerProps>;
            Collection: React.FC<FormCollectionContainerProps>;
        };
    }
): React.FC<DocumentFormViewProps> {
    const {
        UI: {
            Application: { createErrorBoundary },
        },
        Container: { Builder: BuilderContainer, Collection: CollectionContainer },
    } = config;

    const ErrorBoundary = createErrorBoundary({
        errorClass: Error,
    });

    const Layout: React.FC<React.PropsWithChildren<DocumentFormViewProps>> = (props) => (
        <Grid
            h="full"
            w="full"
            gap={{ base: 2, md: 4, xl: 6 }}
            pb={{ base: 24, xl: 32, '2xl': 64 }}
            templateRows="min-content 1fr"
        >
            {/* Header */}
            <GridItem p={{ base: 2, md: 3, lg: 4, xl: 6 }}>
                <HStack w="full" justify="space-between" align="start">
                    <Breadcrumb
                        userSelect="none"
                        separator={<Icon as={HiOutlineSlash} color="whiteAlpha.400" />}
                        spacing={0}
                    >
                        <BreadcrumbItem>
                            <BreadcrumbLink
                                as={Button}
                                color="whiteAlpha.600"
                                bg="none"
                                size="sm"
                                minH={0}
                                textDecor="none"
                                fontWeight="medium"
                                px={2}
                                py={0.5}
                                _hover={{
                                    bg: 'whiteAlpha.50',
                                }}
                            >
                                Workspace
                            </BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbItem>
                            <HStack
                                maxW="20rem"
                                bg="none"
                                fontSize="sm"
                                minH={0}
                                textDecor="none"
                                fontWeight="medium"
                                userSelect="none"
                                cursor="pointer"
                                px={2}
                                py={0.5}
                                borderRadius="md"
                                spacing={2}
                                _hover={{
                                    bg: 'whiteAlpha.50',
                                }}
                            >
                                {props.document.icon.kind === 'emoji' && (
                                    <Text fontSize="md">{props.document.icon.text}</Text>
                                )}
                                {props.document.icon.kind === 'icon' && (
                                    <Icon
                                        fontSize="md"
                                        color="whiteAlpha.600"
                                        as={props.document.icon.Component}
                                    />
                                )}
                                <Text
                                    noOfLines={1}
                                    overflow="hidden"
                                    w="full"
                                    textOverflow="ellipsis"
                                    color="whiteAlpha.600"
                                >
                                    {props.document.label}
                                </Text>
                            </HStack>
                        </BreadcrumbItem>
                    </Breadcrumb>

                    <HStack spacing={3}>
                        <AvatarGroup userSelect="none" size="sm" max={2} spacing={-3}>
                            <Avatar name="Agent Susie" bg="blue.300" />
                            <Avatar name="You" bg="green.300" />
                            <Avatar name="Agent Jane" bg="red.300" />
                        </AvatarGroup>
                        <HStack spacing={1}>
                            {props.actions.map((action) => (
                                <Tooltip
                                    {...action.tooltip}
                                    key={action.id}
                                    placement="bottom"
                                    openDelay={300}
                                    px={2}
                                    py={1}
                                    bg="gray.50"
                                    color="gray.900"
                                    borderRadius="md"
                                    closeOnClick={false}
                                    shouldWrapChildren={true}
                                >
                                    <IconButton
                                        {...action.button}
                                        onFocus={(event) => event.preventDefault()}
                                        aria-label={action.id}
                                        size="sm"
                                        bg="none"
                                        _hover={{
                                            bg: action.button.isDisabled
                                                ? 'none'
                                                : 'whiteAlpha.50',
                                        }}
                                        _disabled={{
                                            opacity: 0.4,
                                            cursor: 'not-allowed',
                                        }}
                                        icon={
                                            <Icon
                                                fontSize="lg"
                                                color={
                                                    action.button.isDisabled
                                                        ? 'whiteAlpha.400'
                                                        : 'whiteAlpha.800'
                                                }
                                                as={action.Icon}
                                            />
                                        }
                                    />
                                </Tooltip>
                            ))}
                        </HStack>
                        <ButtonGroup
                            isAttached={true}
                            borderWidth={1}
                            borderColor="whiteAlpha.300"
                            variant="outline"
                            borderRadius="full"
                            overflow="hidden"
                        >
                            <Button
                                color="whiteAlpha.800"
                                bg="none"
                                size="sm"
                                _hover={{
                                    bg: 'whiteAlpha.50',
                                }}
                                borderRightWidth={1}
                                borderRightColor="whiteAlpha.300"
                            >
                                Share
                            </Button>
                            <IconButton
                                aria-label="more"
                                bg="none"
                                size="sm"
                                _hover={{
                                    bg: 'whiteAlpha.50',
                                }}
                                icon={<Icon fontSize="lg" as={ChevronDownIcon} />}
                            />
                        </ButtonGroup>
                    </HStack>
                </HStack>
            </GridItem>
            <GridItem>
                <VStack
                    // TODO keep in sync with offset in document editor
                    // pl={9}
                    w="full"
                    maxW={{ base: '56rem', '2xl': '72rem' }}
                    mx="auto"
                    position="relative"
                    align="start"
                    spacing={4}
                    // right={styles.gutter.width + styles.gutter.spacing}
                    pb={{ base: 24, md: 32, lg: 40, xl: 48, '2xl': 64 }} // needs to match the collection item hover menu relative offset
                    px={6}
                >
                    {props.children}
                </VStack>
            </GridItem>
        </Grid>
    );

    return (props) => {
        return (
            <Layout {...props}>
                <VStack
                    w="full"
                    align="start"
                    spacing={8}
                    px={{ base: 2, md: 4, xl: 6, '2xl': 8 }}
                    // pl={styles.gutter.width + styles.gutter.spacing}
                >
                    {props.document.icon.kind === 'emoji' && (
                        <Box
                            fontSize="7xl"
                            width="1em"
                            height="1em"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            borderRadius="md"
                            cursor="pointer"
                            _hover={{ bg: 'whiteAlpha.50' }}
                        >
                            <Text lineHeight={1}>{props.document.icon.text}</Text>
                        </Box>
                    )}
                    <HStack w="full" justify="space-between">
                        <HStack spacing={4} align="start">
                            <Heading
                                as="h1"
                                fontWeight="semibold"
                                fontSize="3xl"
                                color="whiteAlpha.900"
                            >
                                {props.document.label}
                            </Heading>
                        </HStack>
                        {/* <HStack spacing={4} align="end">
                            <Badge size="xl">Draft</Badge>
                        </HStack> */}
                    </HStack>
                </VStack>
                <Tabs
                    {...props.tab.root}
                    variant="soft-rounded"
                    w="full"
                    px={{ base: 2, md: 4, xl: 6, '2xl': 8 }}
                >
                    <TabList as={HStack} spacing={2}>
                        {props.tab.items.map((item) => (
                            <Tab
                                key={item.id}
                                h={8}
                                bg="none"
                                color="whiteAlpha.500"
                                _hover={{ bg: 'whiteAlpha.50' }}
                                _selected={{
                                    bg: 'whiteAlpha.100',
                                    color: 'whiteAlpha.800',
                                }}
                            >
                                <HStack spacing={2}>
                                    <Icon as={item.Icon} />
                                    <Text>{item.label}</Text>
                                </HStack>
                            </Tab>
                        ))}
                    </TabList>

                    <ErrorBoundary fallback={(info) => <div>{info.error.message}</div>}>
                        <TabPanels>
                            {props.tab.items.map((item) => (
                                <TabPanel
                                    key={item.id}
                                    id={item.id}
                                    as={VStack}
                                    p={0}
                                    spacing={8}
                                    w="full"
                                    align="start"
                                    maxW={{ base: '56rem', '2xl': '72rem' }}
                                    mx="auto"
                                >
                                    {item.id === 'interview' && (
                                        <BuilderContainer {...props.builder} />
                                    )}
                                    {item.id === 'responses' && (
                                        <CollectionContainer {...props.collection} />
                                    )}
                                </TabPanel>
                            ))}
                        </TabPanels>
                    </ErrorBoundary>
                </Tabs>
            </Layout>
        );
    };
}
